import './ChapterPanel.scss';
import React, { useEffect, useRef, useCallback, useContext } from "react";
import { SlideList } from "../SlideList/SlideList";
import { I18nReactContext } from "../../../../contexts/I18nContext";
import { CourseEditorContext } from "../../../../contexts/CourseEditorContext";
import AddIcon from '@mui/icons-material/Add';
import { COURSE_MODES } from "../../../../contexts/CourseEditorContext";


/**
 * ChapterPanel - Displays a list of course chapters/sections with add/edit capabilities.
 * Gets sections, isGeneratingChapter, and mode from CourseEditorContext.
 *
 * @param {function({chapterId: string}): void} onChapterSelected - Callback when a chapter is selected
 * @param {string} selectedChapterId - ID of currently selected chapter
 * @param {function(): void} [onAddChapter] - Optional handler to add a new chapter
 */
export const ChapterPanel = ({
    onChapterSelected,
    selectedChapterId,
    onAddChapter
}) => {
    const contentRef = useRef(null);
    const { i18n } = useContext(I18nReactContext);

    // Get values from CourseEditorContext
    const courseEditorContext = useContext(CourseEditorContext);
    const {
        sections: courseData = [],
        isGeneratingChapter = false,
        mode
    } = courseEditorContext || {};

    // Derive isEditMode from mode
    const isEditMode = mode === 'edit';

    // Scroll to bottom when a new chapter is being generated
    useEffect(() => {
        if (isGeneratingChapter && contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
    }, [isGeneratingChapter, courseData.length]);

    const getItemId = useCallback((chapter) => chapter.id, []);

    const handleSelect = useCallback(({ id }) => {
        if (onChapterSelected) onChapterSelected({ chapterId: id });
    }, [onChapterSelected]);
    const handleEditTitle = useCallback((id, newTitle) => {
        if (!courseEditorContext || !courseEditorContext.updateSection) return;
        const chapter = courseData.find(chapter => chapter.id === id);
        if (!chapter) return;
        courseEditorContext.updateSection({ ...chapter, title: newTitle });
    }, [courseEditorContext, courseData]);

    const handleDuplicate = useCallback((id) => {
        if (!courseEditorContext || !courseEditorContext.duplicateSection) return;
        courseEditorContext.duplicateSection(id, '');
    }, [courseEditorContext]);

    const handleRemove = useCallback((id) => {
        if (!courseEditorContext || !courseEditorContext.deleteSection) return;

        // Determine which slide to select after deletion: previous item if exists, else next
        const index = courseData.findIndex(c => c.id === id);
        let targetId = null;
        if (index > 0) {
            targetId = courseData[index - 1]?.id || null;
        } else if (courseData.length > 1) {
            // Was the first item; select the new first item (current second becomes first)
            targetId = courseData[1]?.id || null;
        }

        courseEditorContext.deleteSection(id);

        if (targetId && courseEditorContext.selectSection) {
            // Defer selection until after sections update
            setTimeout(() => {
                courseEditorContext.selectSection(targetId);
            }, 0);
        }
    }, [courseEditorContext, courseData]);

    return (
        <div className={`ChapterPanel`}>
            <div className="PanelWrapper" ref={contentRef}>
            <div className="chapterPanelHeader">
                    <div className="header-title">
                        {i18n.translate('weezbeeCourse.chapterPanel.title')} <span className="chapterCount">({courseData.length})</span>
                    </div>
                    {mode === COURSE_MODES.EDIT && (
                        <button 
                            className="add-chapter-button"
                            onClick={() => onAddChapter && onAddChapter()}
                        >
                            <AddIcon fontSize="small" />
                        </button>
                    )}
                </div>
                <SlideList
                    items={courseData}
                    getItemId={getItemId}
                    selectedItemId={selectedChapterId}
                    onSelect={handleSelect}
                    onAddItem={onAddChapter}
                    onEditItemTitle={handleEditTitle}
                    onDuplicateItem={handleDuplicate}
                    onRemoveItem={handleRemove}
                />
                {isGeneratingChapter && (
                    <div className="slide-thumb generating" key="generating-placeholder" />
                )}
            </div>
        </div>
    );
}
